// Advanced textarea that will allow to have:
// - cross-browser multilines placeholder (used in SendFeedModal)
// - html element as a placeholder (used in TokenInput)

import * as React from 'react';
import {defaults} from 'lodash';
import {controlReset} from './constants/Styles';
import {
  styledDiv,
  useTheme,
  forwardHtmlProps,
  tokens,
} from '@packages/utilities/styled';
import Hitarea from './Hitarea';
import type {CSSProperties} from '@packages/utilities/css-properties-type';

const TextareaHolder = styledDiv(
  ({theme, style}) => ({
    position: 'relative',
    background: theme.TextInput.background,
    borderWidth: theme.control.borderWidth,
    borderStyle: 'solid',
    borderColor: theme.TextInput.borderColor,
    borderRadius: theme.control.borderRadius,
    overflow: 'hidden',
    boxShadow: theme.TextInput.boxShadow,
    ...style,
  }),
  {
    focus: ({theme: {focusColors, TextInput}}) => ({
      boxShadow: [TextInput.boxShadow, focusColors.outline].join(','),
      background: TextInput.focus.background,
    }),
    hover: ({theme: {TextInput}}) => ({
      background: TextInput.hover.background,
      borderColor: TextInput.borderColorHover,
    }),
    invalid: ({theme: {focusColors, TextInput}}) => ({
      boxShadow: [TextInput.boxShadow, focusColors.errorShadow].join(','),
      borderColor: 'transparent',
    }),
  }
);

const Placeholder = styledDiv(
  ({theme, style, placeholderStyles}) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    padding: theme.gutters.narrow,
    boxSizing: 'border-box',
    color: tokens.colors.textDimmed,
    whiteSpace: 'pre-wrap',
    fontSize: style?.fontSize,
    ...placeholderStyles,
  }),
  {
    oneline: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
    },
  }
);

type TextareaProps = {
  ['data-automation-id']?: string;
  focus?: boolean;
  invalid?: boolean;
  text: string;
  value?: string;
  placeholderValue?: string | React.ReactElement<any>;
  placeholderStyles?: {
    [key: string]: any;
  };
  style?: CSSProperties;
  getTextarea?: (arg1?: HTMLTextAreaElement | null | undefined) => void;
  oneline?: boolean;
  hover?: boolean;
  onChange?: (arg1: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (
    arg1: React.KeyboardEvent<HTMLInputElement>
  ) => boolean | undefined;
  onEnter?: (arg1?: any) => void;
  onBlur?: (arg1?: any) => void;
  onFocus?: (arg1?: any) => void;
  selectAll?: boolean;
  rows?: number;
  placeholder?: string;
  id?: string;
};

/**
 * @deprecated use `Textarea` from `@packages/systems/spring` instead
 */
export const Textarea = (props: TextareaProps) => {
  const {
    placeholderValue,
    text,
    'data-automation-id': dataAutomationId,
  } = props;
  const holderStyles = {
    height:
      // @ts-expect-error - TS2365 - Operator '+' cannot be applied to types '{}' and 'number'.
      props.style && props.style.height ? props.style.height + 2 : 'initial',
    width: props.style && props.style.width ? props.style.width : 'initial',
  } as const;

  const theme = useTheme();

  const textareaStyles = {
    paddingTop: theme.gutters.narrow,
    paddingRight: theme.gutters.narrow,
    paddingBottom: theme.gutters.narrow,
    paddingLeft: theme.gutters.narrow,
    boxSizing: 'border-box',
    resize: 'vertical',
    width: '100%',
    position: 'relative',
    ...props.style,
  } as const;

  const setRef = (textarea?: HTMLTextAreaElement | null) => {
    if (props.getTextarea) {
      props.getTextarea(textarea);
    }
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    props.onChange && props.onChange(e);

  const onKeyDown = (e: any) => props.onKeyDown && props.onKeyDown(e);

  const onEnter = () => props.onEnter && props.onEnter();

  const onBlur = () => props.onBlur && props.onBlur();

  const onFocus = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (props.onFocus) {
      props.selectAll && e.target.select();
      props.onFocus(e);
    }
  };

  const htmlProps = forwardHtmlProps(props, 'textarea');

  return (
    <Hitarea>
      <TextareaHolder
        focus={props.focus}
        hover={props.hover}
        invalid={props.invalid}
        style={holderStyles}
        data-legacy-ds="Textarea"
      >
        {text
          ? null
          : placeholderValue && (
              <Placeholder
                style={props.style}
                oneline={props.oneline}
                placeholderStyles={props.placeholderStyles}
              >
                {placeholderValue}
              </Placeholder>
            )}
        <textarea
          data-automation-id={dataAutomationId}
          value={!props.value ? text : undefined}
          ref={setRef}
          {...htmlProps}
          style={defaults(textareaStyles, controlReset)}
          // @ts-expect-error - TS2322 - Type '(e: React.ChangeEvent<HTMLInputElement>) => boolean | undefined' is not assignable to type 'ChangeEventHandler<HTMLTextAreaElement>'.
          onChange={onChange}
          onKeyDown={onKeyDown}
          // eslint-disable-next-line react/no-unknown-property
          onEnter={onEnter}
          onBlur={onBlur}
          // @ts-expect-error - TS2322 - Type '(e: React.ChangeEvent<HTMLInputElement>) => void' is not assignable to type 'FocusEventHandler<HTMLTextAreaElement>'.
          onFocus={onFocus}
        />
      </TextareaHolder>
    </Hitarea>
  );
};

Textarea.displayName = 'Textarea';
export default Textarea;
